import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, interval, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { ActiveSessionsService } from '../active-sessions/active-sessions.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { SnackbarService } from '../snackbar.service';
import { UserSessionService } from '../user-session/user-session.service';

@Injectable({ providedIn: 'root' })
export class HIPAASessionTimeoutService {
    private readonly warningBufferSeconds = 60;
    private readonly heartbeatInterval = 420;
    public readonly maxInactivityDurationSeconds = 900;

    private logoutDate: Date = new Date(0);
    private warningDate: Date = new Date(0);
    private warningShownDate: Date = new Date(0);
    private listenersAdded = false;
    private heartbeatSubscription: Subscription | null = null;

    constructor(
        private authorizationService: AuthorizationService,
        private snackbarService: SnackbarService,
        private userSessionService: UserSessionService,
        private router: Router,
        private activeSessionsService: ActiveSessionsService,
    ) {}

    initializeSessionManagement(): void {
        if (this.userSessionService.getIsLoggedIn()) {
            this.resetInactivityTimer();
            this.addUserActivityListeners();
            this.startHeartbeatTimer();
        }
    }

    private startHeartbeatTimer(): void {
        this.stopHeartbeatTimer();
        this.heartbeatSubscription = interval(this.heartbeatInterval * 1000).subscribe(() => {
            if (this.userSessionService.getIsLoggedIn()) {
                this.sendHeartbeat();
            } else {
                this.stopHeartbeatTimer();
            }
        });
        setTimeout(() => {
            this.sendHeartbeat();
        }, 5000);
    }

    private stopHeartbeatTimer(): void {
        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
            this.heartbeatSubscription = null;
        }
    }

    private sendHeartbeat(): void {
        this.activeSessionsService.sendHeartbeat().subscribe({
            next: () => {},
            error: (err) => {
                console.error('Failed to send heartbeat:', err);
                const wind = window as Window;

                wind.location.href = '/login?sessionExpired=true';
            },
        });
    }

    private resetInactivityTimer(): void {
        const currentDate = new Date();

        this.logoutDate = new Date(currentDate.getTime() + this.maxInactivityDurationSeconds * 1000);
        this.warningDate = new Date(this.logoutDate.getTime() - this.warningBufferSeconds * 1000);
    }

    private showWarning() {
        const currentDate = new Date();

        if (currentDate.getTime() < this.warningShownDate.getTime() + this.warningBufferSeconds * 1000) {
            return;
        }
        this.warningShownDate = new Date();
        const timePassed = currentDate.getTime() - this.warningDate.getTime();
        const bufferTimeLeft = this.logoutDate.getTime() - this.warningDate.getTime() - timePassed;

        this.snackbarService.warn(
            `You will be automatically logged out due to inactivity in ${Math.ceil(bufferTimeLeft / 1000)} seconds.`,
            Math.ceil(bufferTimeLeft),
        );
    }

    checkSessionIsStillValid() {
        const currentDate = new Date();

        if (this.logoutDate.getTime() <= currentDate.getTime()) {
            this.logout();
        } else if (
            this.warningDate.getTime() <= currentDate.getTime() &&
            this.logoutDate.getTime() > currentDate.getTime()
        ) {
            this.showWarning();
        }
    }

    private addUserActivityListeners(): void {
        if (this.listenersAdded) return;
        this.listenersAdded = true;
        const events = [
            fromEvent(window, 'mousemove'),
            fromEvent(window, 'keydown'),
            fromEvent(window, 'click'),
            fromEvent(window, 'touchstart'),
        ];

        merge(...events)
            .pipe(throttleTime(1000))
            .subscribe(() => {
                if (this.userSessionService.getIsLoggedIn()) {
                    this.resetInactivityTimer();
                }
            });

        interval(1000).subscribe(() => {
            if (this.userSessionService.getIsLoggedIn()) {
                this.checkSessionIsStillValid();
            }
        });
    }

    logout() {
        if (this.userSessionService.getIsLoggedIn()) {
            this.stopHeartbeatTimer();
            this.userSessionService.setIsLoggedIn(false);
            this.authorizationService.logout().subscribe({
                complete: () => {
                    const wind = window as Window;

                    wind.location.href = '/login?sessionExpired=true';
                    this.logoutDate = new Date(0);
                    this.warningDate = new Date(0);
                    this.warningShownDate = new Date(0);
                },
                error: (err) => {
                    this.snackbarService.error('An error occurred while logging out.');
                    console.error(err);
                },
            });
        }
    }
}
